import React, { useEffect, useMemo, useState } from 'react';
import { Button, Input, Pagination, Select, Tooltip, Typography } from '@attentive-platform/stem-ui';

import StateLozenge from './StateLozenge';
import useScopeRecords, { PAGE_SIZE_OPTIONS } from './useScopeRecords';
import { fetchSalesTeamMembers, mapSalesTeam } from './api';
import { ALL_USERS_OPTION, DATE_OPTIONS, PILOT_OPTIONS, STATE_OPTIONS } from './constants';
import { IntakeTabStyles as I, TableStyles as T } from './styles';

const COLUMNS = [
  { key: 'email', label: 'Customer email' },
  { key: 'company', label: 'Prospect company' },
  { key: 'state', label: 'State' },
  { key: 'trades', label: 'Trades in scope' },
  { key: 'people', label: 'Creator · AE · FDE' },
  { key: 'created', label: 'Created' },
  { key: 'aging', label: 'Aging' },
  { key: 'pilot', label: 'Pilot' }
];

const IntakeTab = ({ onOpenRow, onNewIntake, refreshToken, searchLocked = false }) => {
  const [query, setQuery] = useState('');
  const [stateFilter, setStateFilter] = useState('All states');
  const [userFilter, setUserFilter] = useState(ALL_USERS_OPTION.value);
  const [pilotFilter, setPilotFilter] = useState('All pilots');
  const [dateFilter, setDateFilter] = useState('Last 90 days');
  const [team, setTeam] = useState({ ae: [], se: [] });

  const userOptions = useMemo(() => {
    const seen = new Set();
    const opts = [ALL_USERS_OPTION];
    [...(team.ae || []), ...(team.se || [])].forEach(u => {
      if (seen.has(u.value)) return;
      seen.add(u.value);
      opts.push({ label: u.label, value: u.value });
    });
    return opts;
  }, [team]);

  useEffect(() => {
    let cancelled = false;
    fetchSalesTeamMembers().then(({ data, error }) => {
      if (cancelled || error) return;
      setTeam(mapSalesTeam(data));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const { rows, count, loading, refetch, page, setPage, pageSize, setPageSize, pageCount } = useScopeRecords({
    query,
    stateFilter,
    userFilter,
    pilotFilter,
    dateFilter
  });
  const pageSizeItems = useMemo(
    () => PAGE_SIZE_OPTIONS.map(n => ({ label: `${n} / page`, value: n })),
    []
  );

  useEffect(() => {
    if (refreshToken !== undefined) refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  const rowsLabel = count === 1 ? '1 intake link' : `${count} intake links`;

  return (
    <T.Wrap>
      <T.Toolbar>
        <T.SearchWide>
          <Input
            size='large'
            placeholder='Search customer email or prospect name'
            value={query}
            onChange={e => setQuery(e.target.value)}
            width='20rem'
            name='intake_list_search'
            inputProps={{
              autoComplete: 'off',
              readOnly: searchLocked,
              'data-1p-ignore': true,
              'data-lpignore': 'true'
            }}
          />
        </T.SearchWide>
        <T.Filters>
          <T.SelectSmall>
            <Select
              size='small'
              variant='std-filled'
              items={STATE_OPTIONS}
              value={stateFilter}
              onChange={setStateFilter}
            />
          </T.SelectSmall>
          <T.SelectWide>
            <Select size='small' variant='std-filled' items={userOptions} value={userFilter} onChange={setUserFilter} />
          </T.SelectWide>
          <T.SelectSmall>
            <Select
              size='small'
              variant='std-filled'
              items={PILOT_OPTIONS}
              value={pilotFilter}
              onChange={setPilotFilter}
            />
          </T.SelectSmall>
          <T.SelectMedium>
            <Select
              size='small'
              variant='std-filled'
              items={DATE_OPTIONS}
              value={dateFilter}
              onChange={setDateFilter}
            />
          </T.SelectMedium>
          <Button variant='contained' color='primary' onClick={onNewIntake}>
            New intake
          </Button>
        </T.Filters>
      </T.Toolbar>

      <T.TableBox>
        <I.HeaderGrid>
          {COLUMNS.map(c => (
            <T.HeaderCell key={c.key}>{c.label}</T.HeaderCell>
          ))}
        </I.HeaderGrid>

        {loading ? (
          <T.EmptySmall>Loading intake links…</T.EmptySmall>
        ) : rows.length === 0 ? (
          <T.EmptyBig>
            <Typography variant='body1' sx={{ fontWeight: 600 }}>
              No intake links yet
            </Typography>
            <Typography variant='body3' color='#666666' sx={{ maxWidth: '44ch', textAlign: 'center' }}>
              Send a customer a scope form and it shows up here — with its state and age.
            </Typography>
            <I.EmptyCtaWrap>
              <Button variant='contained' color='primary' onClick={onNewIntake}>
                New intake
              </Button>
            </I.EmptyCtaWrap>
          </T.EmptyBig>
        ) : (
          rows.map(r => {
            const Row =
              r.pilotStatus === 'closed' ||
              r.pilotStatus === 'closed_won' ||
              r.pilotStatus === 'closed_lost'
                ? I.RowClosed
                : I.RowOpen;
            return (
              <Row key={r.id} onClick={() => onOpenRow && onOpenRow(r.id)}>
                <T.CellCol>
                  <I.EmailText>{r.email}</I.EmailText>
                  {r.submittedEmail && <I.MismatchPill>submitted from {r.submittedEmail}</I.MismatchPill>}
                </T.CellCol>
                <I.CellTrunc>
                  <Typography variant='body3'>{r.company}</Typography>
                </I.CellTrunc>
                <T.Cell>
                  <StateLozenge state={r.state} />
                </T.Cell>
                <T.Cell>
                  <Typography variant='body3' color='#4D4D4D' sx={{ lineHeight: 1.45 }}>
                    {r.state === 'New'
                      ? 'not sent yet'
                      : r.state === 'Sent'
                        ? 'awaiting submission'
                        : r.state === 'Sent · expired' || r.state === 'Reopened · expired'
                          ? 'link expired'
                          : r.tradeList.length
                            ? r.tradeList.slice(0, 2).join(', ') +
                              (r.tradeList.length > 2 ? `, +${r.tradeList.length - 2}` : '')
                            : '—'}
                  </Typography>
                </T.Cell>
                <T.CellStack>
                  <Typography variant='body3'>{`${r.creator} · ${r.ae} · ${r.fde}`}</Typography>
                  {r.cc && r.ccEmails?.length ? (
                    <Tooltip arrow placement='top' variant='dark' title={r.ccEmails.join(', ')}>
                      <div>
                        <Typography variant='body3' color='#808080' sx={{ width: 'fit-content', cursor: 'default' }}>
                          {`${r.cc} CC${r.cc > 1 ? 's' : ''}`}
                        </Typography>
                      </div>
                    </Tooltip>
                  ) : (
                    <Typography variant='body3' color='#808080'>
                      {r.cc ? `${r.cc} CC${r.cc > 1 ? 's' : ''}` : 'no CCs'}
                    </Typography>
                  )}
                </T.CellStack>
                <T.Cell>
                  <Typography variant='body3' color='#4D4D4D'>
                    {r.created}
                  </Typography>
                </T.Cell>
                <T.CellStack>
                  {r.stale ? <I.AgingStale>{r.sinceSent}</I.AgingStale> : <I.AgingCalm>{r.sinceSent}</I.AgingCalm>}
                  <Typography variant='body3' color='#808080'>
                    {r.inState}
                  </Typography>
                </T.CellStack>
                <T.Cell>
                  <Typography variant='body3' color='#4D4D4D'>
                    {r.pilot}
                  </Typography>
                </T.Cell>
              </Row>
            );
          })
        )}
      </T.TableBox>

      <T.FooterRow>
        <Typography variant='body3' color='#666666'>
          {rowsLabel}
        </Typography>
        <T.FooterRight>
          <T.SelectSmall>
            <Select
              size='small'
              variant='std-filled'
              items={pageSizeItems}
              value={pageSize}
              onChange={next => setPageSize(Number(next))}
            />
          </T.SelectSmall>
          {pageCount > 1 && (
            <Pagination
              size='small'
              color='primary'
              count={pageCount}
              page={page}
              onChange={(_, next) => setPage(next)}
            />
          )}
        </T.FooterRight>
      </T.FooterRow>
    </T.Wrap>
  );
};

export default IntakeTab;
